import React, { useCallback, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { IconButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ContactCard, ContactCardLine, WarningCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { DarkSkeleton } from '@eika/skeleton';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useContactInformation } from '../../fetchHooks/useContact';
import type { CustomerAddress, Epost, Telefonnummer } from '../../generated-types';
import { FormContextProvider, useFormContext } from '../../hooks/useForm';
import useTranslation from '../../hooks/useTranslation';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import UpdateContactInformationModal from './UpdateContactInformationModal';
import { UpdateContactInfoAction, UpdateContactInfoActionType } from './form/actions';
import updateContactInfoReducer from './form/reducer';
import { updateContactInfoInitialState, UpdateContactInfoState } from './form/state';
import { Error, useValidate } from './form/validate';
import { cleanupEmail, getAddressJSX, getPhoneNumberJSX } from './format';

import './ContactInformation.scss';

const ContactInformation: React.FC = () => {
    const { signingStatus, handleCancelSigningFlow, dispatch, dynamicLinkId } = useSigningContext();

    const { handleChange } = useFormContext<
        UpdateContactInfoState,
        UpdateContactInfoAction,
        Error,
        UpdateContactInfoActionType
    >();

    const contactInfo = useContactInformation({
        onSuccess: (data) => {
            handleChange({ type: 'email', payload: data.email?.value });
            handleChange({ type: 'mobile', payload: data.phoneNumber?.value });
        },
    });

    const translation = useTranslation();

    const [open, setOpen] = useState(false);

    const resetState = useCallback(
        (email?: string, mobile?: string) => {
            handleChange({ type: 'mobile', payload: mobile ?? contactInfo.data?.phoneNumber?.value });
            handleChange({ type: 'email', payload: email ?? contactInfo.data?.email?.value });
        },
        [handleChange, contactInfo],
    );

    const openModal = useCallback(() => {
        setOpen(true);
        resetState();
    }, [setOpen, resetState]);

    const resetStates = useCallback(() => {
        if (signingStatus === 'COMPLETED') {
            contactInfo.invalidateQuery();
        }
        if (signingStatus === 'PROCESSING' && dynamicLinkId) {
            handleCancelSigningFlow();
        }
        dispatch({ type: Actions.RESET });
        setOpen(false);
        resetState();
    }, [resetState, dispatch, contactInfo, signingStatus, dynamicLinkId, handleCancelSigningFlow]);

    if (contactInfo.isLoading) {
        return (
            <section className="contact-information__container sgw-content">
                <div className="contact-information__title-container--skeleton">
                    <DarkSkeleton height={24} width={200} />
                </div>
                <ContactCard>
                    <Skeleton height={30} width="100%" />
                    <Skeleton height={30} width="100%" />
                    <Skeleton height={36} width="100%" />
                </ContactCard>
            </section>
        );
    }

    const noData =
        !contactInfo.data?.email && !contactInfo.data?.phoneNumber && !contactInfo.data?.address && !contactInfo.error;
    if (noData && contactInfo.isFetched) {
        return (
            <section className="contact-information__container sgw-content">
                <WarningCard title={translation.errors.ohNo}>
                    <span data-testid="no-contact-info-found">{translation.contactInfo.errors.foundNoInfo}</span>
                </WarningCard>
            </section>
        );
    }

    if (contactInfo.error) {
        return (
            <Content>
                <ErrorCardWrapper exception={contactInfo.error} title={translation.errors.oops}>
                    <ButtonWithSpinner
                        showSpinner={contactInfo.isLoading}
                        buttonType="positive"
                        onClick={() => contactInfo.refetch()}
                    >
                        {translation.tryAgain}
                    </ButtonWithSpinner>
                </ErrorCardWrapper>
            </Content>
        );
    }

    return (
        <section className="contact-information__container sgw-content">
            {contactInfo.isFetched && (
                <section>
                    <div className="contact-information__title-container">
                        <h3 className="unstyled bold">{translation.contactInfo.contactInfo}</h3>
                        <IconButton
                            data-testid="edit-contact-info-link"
                            onClick={openModal}
                            icon={IconTypes.PENN_24}
                            screenReaderText={translation.contactInfo.editContactInfo}
                        />
                    </div>
                    <ContactCard>
                        <ContactCardLine<Epost>
                            icon={IconTypes.PROFIL_24}
                            value={contactInfo.data?.email}
                            screenReaderLabel={translation.contactInfo.email}
                            formatter={cleanupEmail}
                            placeholder={translation.contactInfo.emailMissing}
                            data-testid="email"
                        />
                        <ContactCardLine<Telefonnummer>
                            icon={IconTypes.TELEFON_24}
                            value={contactInfo.data?.phoneNumber}
                            screenReaderLabel={translation.contactInfo.mobile}
                            formatter={getPhoneNumberJSX}
                            placeholder={translation.contactInfo.mobileMissing}
                            data-testid="phone-number"
                        />
                        <ContactCardLine<CustomerAddress>
                            icon={IconTypes.HUS_24}
                            value={contactInfo.data?.address}
                            screenReaderLabel={translation.contactInfo.address}
                            formatter={getAddressJSX('contact-information__address-wrapper')}
                            placeholder={translation.contactInfo.addressMissing}
                            data-testid="address"
                        />
                    </ContactCard>
                </section>
            )}
            <UpdateContactInformationModal
                open={open}
                setOpen={setOpen}
                onClose={resetStates}
                isLoading={contactInfo.isLoading}
                contactInfo={contactInfo.data}
            />
        </section>
    );
};

export default () => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.UPDATE_CONTACT_INFO}>
        <FormContextProvider
            validateHook={useValidate}
            reducer={updateContactInfoReducer}
            initialState={updateContactInfoInitialState}
        >
            <ContactInformation />
        </FormContextProvider>
    </SigningContextProvider>
);
